import { SagaIterator } from 'redux-saga'
import { takeEvery, all, put, call, takeLatest, cancelled, select } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { ApiError, CartoApiError, CartoCatalogErrorCode, RequestStatus } from 'features/common'
import {
  addToExpandedNodes,
  DATA_TREE_RESOURCES_FROM_PATH_REQUEST,
  DATA_TREE_TILESET_STATS_FETCH_REQUEST,
  deleteDataExplorerTreeLoadingNode,
  fetchTilesetStatsRequest,
  setCurrentConnectionId,
  setCurrentTable,
  setCurrentTableDataPreview,
  setCurrentTableDataPreviewError,
  setCurrentTableDataPreviewStatus,
  setCurrentTableError,
  setCurrentTableStatus,
  setCurrentTableResourceStatsStatus,
  setCurrentTileset,
  setCurrentTilesetError,
  setCurrentTilesetStats,
  setCurrentTilesetStatsStatus,
  setCurrentTilesetStatus,
  setCurrentRaster,
  setCurrentRasterStatus,
  setCurrentRasterError,
  setDataExplorerNodeNotFound,
  setDataExplorerTree,
  setDataExplorerTreeError,
  setDataExplorerTreeLoadingNode,
  setDataExplorerTreeStatus,
  setCurrentTilesetStatsError,
  fetchDataTreeResourceByLink,
  getTableStatsRequest,
  getSubscriptionInfoRequest,
  deleteResourceRequest,
  deleteResourceStatus,
  deleteResourceError,
  deleteResource,
  reloadDataTreeItemRequest,
  isItemLoadedInDataExplorerTree,
  fetchDataTreeResourceRequest,
  fetchDataExplorerObjectRequest,
  fetchTablePreviewRequest,
  setCurrentTableForEnrich,
  setCurrentTableForEnrichStatus,
  getSubscriptionForEnrichRequest,
  getTableStatsForEnrichRequest,
  resetTableForEnrichRequest,
  checkTableResourceStatsRequest,
  setDataExplorerSearchNotFound,
  updateDataExplorerTreeConnection,
  setDataExplorerLoadingNextPage,
  setDataExplorerSearching,
  deleteExpandedNode
} from './dataExplorerSlice'
import {
  DataExplorerTable,
  DataExplorerResource,
  DataTreeResourceRequestProps,
  DataExplorerTreeItem,
  TablePreviewOpts,
  TreeObjectType,
  Tileset,
  TilesetSizeByZoom,
  Raster,
  FieldType,
  DataExplorerSubscription,
  SchemaField,
  defaultGeomColumn,
  DataTreeSubscriptionInfoRequestProps,
  DataTreeItemTypes,
  SchemaFieldType,
  Dataset,
  DatasetVariables
} from '../types'
import {
  getObjectInfoLink,
  getDataExplorerObject,
  getDataTreeResource,
  getTilesetResourceStats,
  getTablePreviewDataViaSQL,
  getSubscriptionResource,
  getTableResourceStats,
  getDatasetDetails,
  getDatasetVariables,
  deleteResourcePostRequest
} from '../api/dataExplorer'
import { isValidGeom } from '../utils'
import { getNestedPropertyFrom } from 'features/common/utils/objects'
import {
  addConnections,
  Connection,
  getConnectionByName,
  ProviderType,
  updateConnectionFromList
} from 'features/connections'
import { createApiError } from 'features/common/api/apiError'
import { ProcessingJob, ProcessingType } from 'features/jobs/types'

type DataExplorerObjectRequest = {
  link: string
  object?: TreeObjectType
  fetchDataPreview?: boolean
  connectionName?: string
}

// Map to keep track of active requests
const activeRequests = new Map<string, AbortController>()

function dataExplorerObjectFromChildren(resource: DataExplorerResource, onlyGeomData?: boolean) {
  let response: Record<string, DataExplorerResource> = {}
  if (resource?.children) {
    for (const object of resource.children) {
      const filteredByGeomData = onlyGeomData && object.type === 'table' && (!object.geomField || !isValidGeom(object))
      if (!object.id || filteredByGeomData) continue
      const resource: Record<string, DataExplorerResource> = {}
      const latest = object.id.split('.').pop()

      if (latest) {
        resource[latest] = object
      }
      response = { ...response, ...resource }
    }
  }
  return response
}

function* fetchDataExplorerObject(action: PayloadAction<DataExplorerObjectRequest>): SagaIterator {
  const { link, object, fetchDataPreview, connectionName } = action.payload
  const controller = new AbortController()
  const { signal } = controller
  const existingController = activeRequests.get(link)

  if (existingController) {
    existingController.abort()
    activeRequests.delete(link)
  }
  activeRequests.set(link, controller)

  try {
    yield put(setDataExplorerSearchNotFound(false))
    yield put(setCurrentTable(null))
    yield put(setCurrentTileset(null))
    yield put(setCurrentRaster(null))
    yield put(setDataExplorerSearchNotFound(false))

    if (object === TreeObjectType.Tileset) {
      yield put(setCurrentTilesetStatus(RequestStatus.Pending))
      const tilesetObject: Tileset = yield call(getDataExplorerObject, { ...action.payload, signal })
      yield put(setCurrentTileset(tilesetObject))
      yield put(setCurrentTilesetStatus(RequestStatus.Fulfilled))
    } else if (object === TreeObjectType.Raster) {
      yield put(setCurrentRasterStatus(RequestStatus.Pending))
      const tilesetObject: Raster = yield call(getDataExplorerObject, { ...action.payload, signal })
      yield put(setCurrentRaster(tilesetObject))
      yield put(setCurrentRasterStatus(RequestStatus.Fulfilled))
    } else if (object === TreeObjectType.Table || object === TreeObjectType.View) {
      yield put(setCurrentTableStatus(RequestStatus.Pending))
      yield put(setCurrentTableResourceStatsStatus(RequestStatus.Idle))
      const tableObject: DataExplorerTable = yield call(getDataExplorerObject, { ...action.payload, signal })
      yield put(setCurrentTable(tableObject))

      if (fetchDataPreview && connectionName) {
        yield put(
          fetchTablePreviewRequest({
            connectionName,
            provider: tableObject.provider,
            table: tableObject.id,
            schema: tableObject.schema
          })
        )
      }
      yield put(setCurrentTableStatus(RequestStatus.Fulfilled))
    }
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      return
    }

    if (object === TreeObjectType.Tileset) {
      yield put(setCurrentTilesetStatus(RequestStatus.Rejected))
      yield put(setCurrentTilesetError(e as CartoApiError))
    } else if (object === TreeObjectType.Raster) {
      yield put(setCurrentRasterStatus(RequestStatus.Rejected))
      yield put(setCurrentRasterError(e as CartoApiError))
    } else if (object === TreeObjectType.Table || object === TreeObjectType.View) {
      yield put(setCurrentTableStatus(RequestStatus.Rejected))
      yield put(setCurrentTableError(e as CartoApiError))
    }
  } finally {
    activeRequests.delete(link)

    if (yield cancelled()) {
      controller.abort()
    }
  }
}

const requestQueue: Array<DataExplorerObjectRequest> = []
let isProcessingQueue = false

function* processRequestQueue() {
  if (isProcessingQueue) {
    return
  }

  isProcessingQueue = true
  try {
    while (requestQueue.length > 0) {
      const request = requestQueue[0]
      try {
        yield call(fetchDataExplorerObject, {
          type: fetchDataExplorerObjectRequest.type,
          payload: request
        })
        requestQueue.shift()
      } catch (error) {
        console.error('Error processing request in queue:', error)
        requestQueue.shift()
        continue
      }
    }
  } catch (error) {
    console.error('Error in processRequestQueue:', error)
    yield put(setDataExplorerTreeError(error as CartoApiError))
  } finally {
    isProcessingQueue = false
  }
}

function* fetchDataTreeResource(action: PayloadAction<DataTreeResourceRequestProps>) {
  try {
    yield put(setDataExplorerSearchNotFound(false))
    yield put(setDataExplorerTreeError(null))
    yield put(setDataExplorerLoadingNextPage((action.payload.page || 1) > 1))
    yield put(setDataExplorerTreeStatus(RequestStatus.Pending))
    yield put(setDataExplorerTreeLoadingNode(action.payload.targetKey))
    const resource: DataExplorerResource = yield call(getDataTreeResource, action.payload)
    const data: Record<string, DataExplorerResource> = dataExplorerObjectFromChildren(resource)
    yield put(
      setDataExplorerTree({
        data,
        targetKey: action.payload.targetKey,
        totalChildren: resource.totalChildren,
        isEmpty: !resource?.children?.length,
        unknownTotal: resource.unknownTotal
      })
    )
    yield put(deleteDataExplorerTreeLoadingNode(action.payload.targetKey))
    yield put(setDataExplorerLoadingNextPage(false))
    yield put(setDataExplorerTreeStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setDataExplorerTreeStatus(RequestStatus.Rejected))
    yield put(
      setDataExplorerTreeError({
        ...(e as CartoApiError),
        frontendMetadata: { connectionId: action.payload.connectionId }
      })
    )
    yield put(deleteDataExplorerTreeLoadingNode(action.payload.targetKey))
    yield put(deleteExpandedNode(action.payload.targetKey.replace(/.children$/, '')))
  }
}

function* fetchDataTreeResourceLink(
  action: PayloadAction<{
    link: string
    targetKey: string
    onlyGeomData?: boolean
    page?: number
    search?: string
    persistSearch?: boolean
    totalChildren?: number
    unknownTotal?: boolean
    refresh?: boolean
    connectionId?: string
  }>
) {
  try {
    yield put(setDataExplorerTreeLoadingNode(action.payload.targetKey))
    yield put(setDataExplorerSearchNotFound(false))
    if (action.payload.search) {
      yield put(setDataExplorerSearching(true))
    }
    yield put(setDataExplorerLoadingNextPage((action.payload.page || 1) > 1))
    yield put(setDataExplorerTreeStatus(RequestStatus.Pending))

    const resources: DataExplorerResource = yield call(getObjectInfoLink, action.payload)
    const data: Record<string, DataExplorerResource> = dataExplorerObjectFromChildren(
      resources,
      action.payload.onlyGeomData
    )
    const dataExplorerTree = { data, ...action.payload, page: resources.page, isEmpty: !resources?.children?.length }
    if (!action.payload.search) {
      dataExplorerTree.totalChildren = resources.totalChildren
      dataExplorerTree.unknownTotal = resources.unknownTotal
    }
    if (action.payload.refresh) {
      dataExplorerTree.refresh = action.payload.refresh
      if (
        (resources.type === DataTreeItemTypes.Table || resources.type === DataTreeItemTypes.View) &&
        resources?.provider &&
        resources.id &&
        resources?.schema
      ) {
        const currentConnection = yield select((state) => state.connections.currentConnection)
        if (currentConnection) {
          yield put(
            fetchTablePreviewRequest({
              connectionName: currentConnection.name,
              provider: resources.provider as ProviderType,
              table: resources.id,
              schema: resources.schema,
              refresh: true
            })
          )
        }
      }
    }
    yield put(setDataExplorerTree(dataExplorerTree))
    yield put(setDataExplorerLoadingNextPage(false))
    yield put(setDataExplorerTreeStatus(RequestStatus.Fulfilled))
    if (action.payload.search) {
      yield put(setDataExplorerSearching(false))
      if (resources.children?.length === 0 && action.payload.persistSearch) {
        yield put(setDataExplorerSearchNotFound(true))
      }
    }

    yield put(deleteDataExplorerTreeLoadingNode(action.payload.targetKey))
  } catch (e) {
    yield put(setDataExplorerTreeStatus(RequestStatus.Rejected))
    yield put(
      setDataExplorerTreeError({
        ...(e as CartoApiError),
        frontendMetadata: { connectionId: action.payload.connectionId || '' }
      })
    )
    yield put(setDataExplorerTree({ data: createApiError(e), targetKey: action.payload.targetKey }))
    yield put(deleteDataExplorerTreeLoadingNode(action.payload.targetKey))
  }
}

function* reloadDataTreeItem(
  action: PayloadAction<{
    connectionId: Connection['id']
    provider_id: Connection['provider_id']
    items: Array<DataExplorerTreeItem['id']>
  }>
) {
  const { connectionId, provider_id, items } = action.payload

  try {
    yield put(setDataExplorerSearchNotFound(false))
    const isDataTreeNodeLoaded = yield select(
      isItemLoadedInDataExplorerTree({
        provider: provider_id,
        connectionId: connectionId,
        items
      })
    )

    if (isDataTreeNodeLoaded) {
      const childrenTargetKey = items.reduce((prev, current) => {
        return `${prev}.${current}.children`
      }, '.children')

      yield put(
        fetchDataTreeResourceByLink({
          link: `/connections/${connectionId}/resources/${items.join('.')}`,
          targetKey: `${provider_id}.${connectionId}${childrenTargetKey}`,
          connectionId
        })
      )
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

function* fetchTilesetStats(action: PayloadAction<{ connectionId: string; tilesetId: string }>) {
  try {
    yield put(setCurrentTilesetStatsStatus(RequestStatus.Pending))
    const tilesetStats: TilesetSizeByZoom[] = yield call(getTilesetResourceStats, action.payload)
    yield put(setCurrentTilesetStats(tilesetStats))
    yield put(setCurrentTilesetStatsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setCurrentTilesetStatsStatus(RequestStatus.Rejected))
    yield put(setCurrentTilesetStatsError(e as ApiError))
  }
}

function* getTileset({
  resourcePath,
  connectionId,
  targetKey,
  link
}: {
  resourcePath: string
  connectionId: string
  targetKey: string
  link?: string
}): SagaIterator {
  if (!link) return
  const controller = new AbortController()
  const { signal } = controller
  try {
    yield put(setDataExplorerSearchNotFound(false))
    yield put(setCurrentTilesetStatus(RequestStatus.Pending))
    yield put(
      fetchTilesetStatsRequest({
        connectionId: connectionId,
        tilesetId: resourcePath
      })
    )
    const tilesetObject: Tileset = yield call(getDataExplorerObject, { link, signal })
    yield put(setCurrentTileset(tilesetObject))
    yield put(setCurrentTilesetStatus(RequestStatus.Fulfilled))
    yield put(setCurrentConnectionId(connectionId))
  } catch (e) {
    yield put(setCurrentTilesetStatus(RequestStatus.Rejected))
    yield put(setCurrentTilesetError(e as CartoApiError))
  } finally {
    if (yield cancelled()) {
      yield put(deleteDataExplorerTreeLoadingNode(targetKey))
      controller.abort()
    }
  }
}

function* getRaster({
  connectionId,
  targetKey,
  link
}: {
  resourcePath: string
  connectionId: string
  targetKey: string
  link?: string
}): SagaIterator {
  if (!link) return
  const controller = new AbortController()
  const { signal } = controller
  try {
    yield put(setDataExplorerSearchNotFound(false))
    yield put(setCurrentTilesetStatus(RequestStatus.Pending))
    const rasterObject: Raster = yield call(getDataExplorerObject, { link, signal })
    yield put(setCurrentRaster(rasterObject))
    yield put(setCurrentRasterStatus(RequestStatus.Fulfilled))
    yield put(setCurrentConnectionId(connectionId))
  } catch (e) {
    yield put(setCurrentRasterStatus(RequestStatus.Rejected))
    yield put(setCurrentRasterError(e as CartoApiError))
  } finally {
    if (yield cancelled()) {
      yield put(deleteDataExplorerTreeLoadingNode(targetKey))
      controller.abort()
    }
  }
}

function* getValidatedSchema({ tableObject, resource }) {
  const tableObjectCopy = { ...tableObject }
  try {
    if (!tableObject.schema) {
      const error: CartoApiError = {
        code: CartoCatalogErrorCode.FrontendUnknown,
        status: 422,
        error: `Resource "${resource}" does not have schema`
      } as CartoApiError
      yield put(setCurrentTableStatus(RequestStatus.Rejected))
      yield put(setCurrentTableDataPreviewStatus(RequestStatus.Rejected))
      yield put(setCurrentTableError(error))
      yield put(setCurrentTableDataPreviewError(error))
    } else {
      tableObjectCopy.schema = tableObject.schema.filter((field) => field.type !== FieldType.Variant)
    }
  } catch (e) {
    console.error(e)
  }
  return tableObjectCopy
}

function* getTable({
  resourcePath,
  connectionId,
  targetKey,
  link
}: {
  resourcePath: string
  connectionId: string
  targetKey: string
  link?: string
}): SagaIterator {
  if (!link) return
  const controller = new AbortController()
  const { signal } = controller
  try {
    yield put(setDataExplorerSearchNotFound(false))
    yield put(setCurrentTableStatus(RequestStatus.Pending))
    yield put(setCurrentTableResourceStatsStatus(RequestStatus.Idle))
    let tableObject: DataExplorerTable = yield call(getDataExplorerObject, { signal, link })
    tableObject = yield call(getValidatedSchema, { tableObject, resource: resourcePath })
    yield put(setCurrentTable(tableObject))
    yield put(setCurrentTableStatus(RequestStatus.Fulfilled))
    yield put(setCurrentConnectionId(connectionId))
  } catch (e) {
    yield put(setCurrentTableStatus(RequestStatus.Rejected))
    yield put(setCurrentTableError(e as CartoApiError))
  } finally {
    if (yield cancelled()) {
      yield put(deleteDataExplorerTreeLoadingNode(targetKey))
      controller.abort()
    }
  }
}

function* getObject({ targetKey, link }: { targetKey: string; link?: string }) {
  if (!link) return
  try {
    yield put(setDataExplorerSearchNotFound(false))
    const resources: DataExplorerResource = yield call(getObjectInfoLink, { link })
    const data: Record<string, DataExplorerResource> = dataExplorerObjectFromChildren(resources, false)
    yield put(setDataExplorerTree({ data, targetKey, totalChildren: resources.totalChildren }))
  } catch (e) {
    yield put(setDataExplorerTreeStatus(RequestStatus.Rejected))
    yield put(setDataExplorerTreeError(e as CartoApiError))
  }
}

function* handleTreeItem({
  targetKey,
  target,
  resourcePath,
  connectionId
}: {
  targetKey: string
  target: DataExplorerTreeItem
  resourcePath: string
  connectionId: string
}) {
  try {
    yield put(setDataExplorerSearchNotFound(false))
    yield put(setDataExplorerTreeStatus(RequestStatus.Pending))
    yield put(setDataExplorerTreeLoadingNode(targetKey))

    if (target.type === TreeObjectType.Tileset) {
      yield call(getTileset, { resourcePath, connectionId, targetKey, link: target._links?.resources })
    } else if (target.type === TreeObjectType.Table || target.type === TreeObjectType.View) {
      yield call(getTable, { resourcePath, connectionId, targetKey, link: target._links?.resources })
    } else if (target.type === TreeObjectType.Raster) {
      yield call(getRaster, { resourcePath, connectionId, targetKey, link: target._links?.resources })
    } else {
      yield call(getObject, { targetKey, link: target._links?.resources })
      yield put(addToExpandedNodes(targetKey))
    }

    yield put(setDataExplorerTreeStatus(RequestStatus.Fulfilled))
    yield put(deleteDataExplorerTreeLoadingNode(targetKey))
  } catch (e) {
    yield put(setDataExplorerTreeStatus(RequestStatus.Rejected))
    yield put(setDataExplorerTreeError(e as CartoApiError))
  }
}

function* fetchResourcesFromPath(
  action: PayloadAction<{ connectionId: string; provider: string; resourcePath: string }>
) {
  const { resourcePath, provider, connectionId } = action.payload
  try {
    yield put(setDataExplorerSearchNotFound(false))
    let dataTree: Record<string, Record<string, DataExplorerTreeItem>> = yield select(
      (state) => state.dataExplorer.dataExplorerTree
    )

    const connectionTree = dataTree[provider][connectionId]
    const connectionTreeHasChildren = connectionTree?.children && Object.keys(connectionTree.children).length

    if (connectionTreeHasChildren && resourcePath) {
      let targetKey = `${provider}.${connectionId}.children`
      let target = connectionTree.children
      const resources = resourcePath.split('.')

      for (const resource of resources) {
        targetKey += `.${resource}.children`
        const currentTreeNode = getNestedPropertyFrom(dataTree, targetKey)
        const treeNodeAlreadyLoaded = currentTreeNode && Object.keys(currentTreeNode).length
        const targetNodeTree = target && target[resource]

        if (!treeNodeAlreadyLoaded && targetNodeTree && targetNodeTree._links?.resources) {
          yield call(handleTreeItem, { targetKey, target: targetNodeTree, resourcePath, connectionId })
        } else if (target && !target[resource]) {
          yield put(setDataExplorerNodeNotFound(resourcePath))
          break
        }
        dataTree = yield select((state) => state.dataExplorer.dataExplorerTree)
        target = getNestedPropertyFrom(dataTree, targetKey)
      }
    }
  } catch (e) {
    yield put(setDataExplorerTreeStatus(RequestStatus.Rejected))
    yield put(setDataExplorerTreeError(e as CartoApiError))
  }
}

function* fetchTablePreview(action: PayloadAction<TablePreviewOpts>) {
  try {
    yield put(setCurrentTableDataPreviewError(null))
    yield put(setCurrentTableDataPreviewStatus(RequestStatus.Pending))
    const { rows } = yield call(getTablePreviewDataViaSQL, action.payload)
    const data: Record<string, string>[] = rows
    yield put(setCurrentTableDataPreview({ data, schema: action.payload.schema }))
    yield put(setCurrentTableDataPreviewStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setCurrentTableDataPreviewStatus(RequestStatus.Rejected))
    yield put(setCurrentTableDataPreviewError(e as ApiError))
  }
}

function* getSubscriptionInfo(action: PayloadAction<DataTreeSubscriptionInfoRequestProps>) {
  try {
    const { datasetId, slug, subscriptionTable, spatialIndexType, spatialIndexResolution } = action.payload
    yield put(setCurrentTableStatus(RequestStatus.Pending))
    yield put(setCurrentTableResourceStatsStatus(RequestStatus.Idle))
    const subscription: DataExplorerSubscription = yield call(getSubscriptionResource, datasetId)
    subscription.spatialIndexType = spatialIndexType
    subscription.spatialIndexResolution = spatialIndexResolution

    const [metadata, variables]: [Dataset, DatasetVariables] = yield all([
      call(getDatasetDetails, slug),
      call(getDatasetVariables, slug)
    ])

    const schemaFields: SchemaField[] = variables.map((v) => ({
      name: v.columnName,
      type: v.dbType,
      isFilterColumn: v.isFilter
    }))
    schemaFields.push({ name: defaultGeomColumn, type: SchemaFieldType.Geometry })

    yield put(
      setCurrentTable({
        id: subscriptionTable,
        schema: schemaFields,
        name: metadata.name,
        geomField: defaultGeomColumn,
        subscription,
        isSubscription: true,
        slug: slug,
        doDatasetId: metadata.id
      } as DataExplorerTable)
    )
    yield put(setCurrentTableStatus(RequestStatus.Fulfilled))
  } catch (e) {
    console.error(e)
  }
}

function* getTableStats(action: PayloadAction<DataTreeResourceRequestProps>) {
  try {
    const props = action.payload
    yield put(setCurrentConnectionId(props.connectionId))
    yield put(setCurrentTableStatus(RequestStatus.Pending))
    const sample = yield call(getDataTreeResource, props)
    yield put(
      setCurrentTable({ ...sample, isSample: props.isSample, isSubscription: props.isSubscription, slug: props.slug })
    )
    yield put(setCurrentTableStatus(RequestStatus.Fulfilled))
  } catch (e) {
    console.error(e)
  }
}

function* deleteResourceAction(action: PayloadAction<{ connectionName: string; connectionId: string; path: string }>) {
  try {
    yield put(deleteResourceStatus(RequestStatus.Pending))

    const connection = yield select((state) =>
      getConnectionByName(state, { connection: action.payload.connectionName })
    )
    if (connection) {
      yield call(deleteResourcePostRequest, {
        connection,
        table: action.payload.path
      })
    }

    yield put(deleteResourceStatus(RequestStatus.Fulfilled))
    yield put(setCurrentTable(null))
    yield put(setCurrentTableStatus(RequestStatus.Idle))
    yield put(setCurrentTableResourceStatsStatus(RequestStatus.Idle))
    yield put(setCurrentTableDataPreview(null))
    yield put(setCurrentTableDataPreviewStatus(RequestStatus.Idle))
    yield put(setCurrentTileset(null))
    yield put(setCurrentTilesetStatus(RequestStatus.Idle))
    yield put(setCurrentTilesetStats(null))
    yield put(setCurrentTilesetStatsStatus(RequestStatus.Fulfilled))
    yield put(setCurrentRaster(null))
    yield put(setCurrentRasterStatus(RequestStatus.Idle))
    yield put(
      deleteResource({
        connectionId: action.payload.connectionId,
        path: action.payload.path
      })
    )
    yield put(deleteResourceStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(deleteResourceStatus(RequestStatus.Rejected))
    yield put(deleteResourceError(e as ApiError))
  }
}

// Update dataExplorer tree with all connection data each time we receive one.
function* updateAddConnections(action: PayloadAction<Connection[]>) {
  if (!action.payload || action.payload.length === 0) {
    return
  }

  try {
    for (const connection of action.payload) {
      yield put(updateDataExplorerTreeConnection(connection))
    }
  } catch (e) {
    yield put(setDataExplorerTreeError(e as CartoApiError))
  }
}

function* handleUpdateConnectionFromList(action: PayloadAction<Partial<Connection>>) {
  try {
    yield put(updateDataExplorerTreeConnection(action.payload))
  } catch (e) {
    yield put(setDataExplorerTreeError(e as CartoApiError))
  }
}

/**
 * Get Subscription for Enrich feature
 * Can not use getSubscriptionInfo because we need to set a different state object
 * @param action
 */
function* getSubscriptionForEnrich(action: PayloadAction<DataTreeSubscriptionInfoRequestProps>) {
  try {
    // TODO: get also spatialIndexType, spatialIndexResolution from action.payload
    // and use them to pass only the data part to DATAOBS_ENRICH_GRID
    const { datasetId, slug, subscriptionTable } = action.payload
    yield put(setCurrentTableForEnrichStatus(RequestStatus.Pending))
    const subscription: DataExplorerSubscription = yield call(getSubscriptionResource, datasetId)

    const [metadata, variables]: [Dataset, DatasetVariables] = yield all([
      call(getDatasetDetails, slug),
      call(getDatasetVariables, slug)
    ])

    const schemaFields: SchemaField[] = variables.map((v) => ({
      name: v.columnName,
      type: v.dbType,
      isFilterColumn: v.isFilter,
      description: v.description,
      agg_method: v.aggMethod,
      slug: v.slug
    }))
    schemaFields.push({ name: defaultGeomColumn, type: SchemaFieldType.Geometry })

    yield put(
      setCurrentTableForEnrich({
        id: subscriptionTable,
        schema: schemaFields,
        name: metadata.name,
        geomField: defaultGeomColumn,
        subscription,
        isSubscription: true,
        slug: slug,
        doDatasetId: metadata.id
      } as DataExplorerTable)
    )
    yield put(setCurrentTableForEnrichStatus(RequestStatus.Fulfilled))
  } catch (e) {
    console.error(e)
  }
}

function* getTableStatsForEnrich(action: PayloadAction<DataTreeResourceRequestProps>) {
  try {
    const props = action.payload
    yield put(setCurrentTableForEnrichStatus(RequestStatus.Pending))
    const sample = yield call(getDataTreeResource, props)
    yield put(
      setCurrentTableForEnrich({
        ...sample,
        isSample: props.isSample,
        isSubscription: props.isSubscription,
        slug: props.slug
      })
    )
    yield put(setCurrentTableForEnrichStatus(RequestStatus.Fulfilled))
  } catch (e) {
    console.error(e)
  }
}

function* resetTableForEnrich() {
  try {
    yield put(setCurrentTableForEnrichStatus(RequestStatus.Idle))
    yield put(setCurrentTableForEnrich(null))
  } catch (e) {
    console.error(e)
  }
}

function* checkTableResourceStats(action: PayloadAction<{ table: DataExplorerTable; connection: Connection }>) {
  try {
    const { connection, table } = action.payload
    yield put(setCurrentTableResourceStatsStatus(RequestStatus.Pending))
    const { geomType, spatialIndex } = yield call(getTableResourceStats, { connection: connection.id, table: table.id })
    yield put(setCurrentTable({ ...table, geomType, spatialIndexStats: spatialIndex }))
    yield put(setCurrentTableResourceStatsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setCurrentTableResourceStatsStatus(RequestStatus.Rejected))
    console.error(e)
  }
}

export function* updateDataExplorer(job) {
  try {
    if (!job.response.metadata.name) return
    const path = job.response.metadata.name.split('.')
    path.pop()

    const connectionId = job.connection
    const jobConnection: Connection = yield select((state) =>
      state.connections.myConnections.find((c) => c.name === connectionId)
    )
    const currentConnection: Connection | null = yield select((state) => state.connections.currentConnection)
    const currentTable: DataExplorerTable | null = yield select((state) => state.dataExplorer.currentTable)
    const isSameConnection = currentConnection?.id === connectionId
    const isSameTable = currentTable?.id === job.response.metadata.name
    const PROCESSING_NEED_REFRESH = [ProcessingType.Geocode, ProcessingType.EnrichTable]
    const needsRefresh = PROCESSING_NEED_REFRESH.includes(job.response.metadata.processingType)

    if (isSameConnection && isSameTable && needsRefresh) {
      yield call(updateDataset, job, jobConnection)
    } else if (needsRefresh) {
      yield call(updateFolder, path, jobConnection)
    }
  } catch (e) {
    console.error(e)
  }
}

export function* updateFolder(path: string[], connection: Connection) {
  try {
    if (!connection) return
    yield put(
      reloadDataTreeItemRequest({
        connectionId: connection.id,
        provider_id: connection.provider_id,
        items: path
      })
    )
  } catch (e) {
    console.error(e)
  }
}

export function* updateDataset(job: ProcessingJob, connection: Connection) {
  try {
    // fetch data-explorer object to get the updated schema and fetch the data-preview
    yield put(
      fetchDataExplorerObjectRequest({
        link: `/connections/${connection.id}/resources/${job.response.metadata.name}`,
        object: TreeObjectType.Table,
        fetchDataPreview: true,
        connectionName: connection.name
      })
    )
  } catch (e) {
    throw Error(`Could not fetch the resource "${job.response.metadata.name}"`)
  }
}

function* handleFetchDataExplorerObjectRequest(action: PayloadAction<DataExplorerObjectRequest>) {
  try {
    const { link } = action.payload
    const existingController = activeRequests.get(link)
    if (existingController) {
      existingController.abort()
      activeRequests.delete(link)
    }

    if (requestQueue.length > 0 || isProcessingQueue) {
      requestQueue.push(action.payload)
    } else {
      requestQueue.push(action.payload)
      yield call(processRequestQueue)
    }
  } catch (error) {
    console.error('Error in handleFetchDataExplorerObjectRequest:', error)
    requestQueue.length = 0
    isProcessingQueue = false
    yield put(setDataExplorerTreeError(error as CartoApiError))
  }
}

function* watchFetchDataExplorer() {
  yield takeEvery(fetchDataExplorerObjectRequest.type, handleFetchDataExplorerObjectRequest)
  yield takeEvery(DATA_TREE_TILESET_STATS_FETCH_REQUEST, fetchTilesetStats)
  yield takeLatest(DATA_TREE_RESOURCES_FROM_PATH_REQUEST, fetchResourcesFromPath)
  yield takeEvery(fetchTablePreviewRequest, fetchTablePreview)
  yield takeLatest(fetchDataTreeResourceRequest.type, fetchDataTreeResource)
  yield takeEvery(fetchDataTreeResourceByLink.type, fetchDataTreeResourceLink)
  yield takeLatest(getTableStatsRequest.type, getTableStats)
  yield takeLatest(getSubscriptionInfoRequest.type, getSubscriptionInfo)
  yield takeLatest(deleteResourceRequest.type, deleteResourceAction)
  yield takeLatest(reloadDataTreeItemRequest.type, reloadDataTreeItem)
  yield takeLatest(addConnections.type, updateAddConnections)
  yield takeLatest(updateConnectionFromList.type, handleUpdateConnectionFromList)
  yield takeLatest(getSubscriptionForEnrichRequest.type, getSubscriptionForEnrich)
  yield takeLatest(getTableStatsForEnrichRequest.type, getTableStatsForEnrich)
  yield takeLatest(resetTableForEnrichRequest.type, resetTableForEnrich)
  yield takeLatest(checkTableResourceStatsRequest.type, checkTableResourceStats)
}

export default function* dataExplorerSagas() {
  try {
    yield all([watchFetchDataExplorer()])
  } catch (e) {
    //TODO
  }
}
